import React from 'react';
import PropTypes from 'prop-types';
import { LuCalendarDays, LuCircleCheck, LuCircleHelp, LuClock, LuLock, LuPlay } from 'react-icons/lu';
import { useTranslation } from '../../../../../i18n/LanguageContext';
import { span } from '../stageLayout';

const CHAPTERS = ['overview', 'reading', 'day', 'addVisit', 'visitForm', 'tips'];

const Backdrop = ({ children }) => (
  <div className="tw-absolute tw-inset-0 tw-overflow-hidden tw-bg-[#f4f6f9] dark:tw-bg-[#14171c]">
    <span className="tw-absolute -tw-left-24 -tw-top-24 tw-h-[420px] tw-w-[420px] tw-rounded-full tw-bg-teal-400/25 tw-blur-3xl" />
    <span className="tw-absolute -tw-bottom-32 tw-right-0 tw-h-[460px] tw-w-[460px] tw-rounded-full tw-bg-cyan-500/20 tw-blur-3xl" />
    <span className="tw-absolute tw-left-1/2 tw-top-1/3 tw-h-[300px] tw-w-[300px] tw-rounded-full tw-bg-indigo-400/10 tw-blur-3xl" />
    <div className="tw-absolute tw-inset-0 tw-flex tw-flex-col tw-items-center tw-justify-center tw-px-24 tw-text-center">{children}</div>
  </div>
);

Backdrop.propTypes = { children: PropTypes.node };

const Rise = ({ show, delay = 0, children }) => (
  <div
    style={{ transitionDelay: `${delay}ms` }}
    className={`tw-transition-all tw-duration-700 ${show ? 'tw-translate-y-0 tw-opacity-100' : 'tw-translate-y-4 tw-opacity-0'}`}
  >
    {children}
  </div>
);

Rise.propTypes = { show: PropTypes.bool, delay: PropTypes.number, children: PropTypes.node };

/** Intro, the 72-hour rule and the closing card. */
const TitleScene = ({ state, p }) => {
  const { t } = useTranslation();
  const label = (key, vars) => t(`profilePage.tutorial.${key}`, vars);

  if (state.variant === 'deadline') {
    const dot = span(p, 0.15, 0.85);
    return (
      <Backdrop>
        <Rise show>
          <span className="tw-mx-auto tw-mb-5 tw-grid tw-h-16 tw-w-16 tw-place-items-center tw-rounded-2xl tw-bg-gradient-to-br tw-from-amber-400 tw-to-orange-500 tw-text-white tw-shadow-xl tw-shadow-amber-500/30">
            <LuClock className="tw-h-8 tw-w-8" />
          </span>
          <p className="tw-text-4xl tw-font-bold tw-tracking-tight tw-text-slate-900 dark:tw-text-white">{label('stage.deadlineTitle')}</p>
        </Rise>
        <div className="tw-relative tw-mt-16 tw-h-40 tw-w-[980px]">
          <span className="tw-absolute tw-left-0 tw-top-12 tw-h-4 tw-w-[52%] tw-rounded-l-full tw-bg-teal-500/80" />
          <span className="tw-absolute tw-left-[52%] tw-top-12 tw-h-4 tw-w-[30%] tw-bg-amber-400/90" />
          <span className="tw-absolute tw-left-[82%] tw-top-12 tw-h-4 tw-w-[18%] tw-rounded-r-full tw-bg-rose-500/70" />
          <span style={{ left: `calc(${dot * 100}% - 14px)` }} className="tw-absolute tw-top-[38px] tw-h-7 tw-w-7 tw-rounded-full tw-border-4 tw-border-white tw-bg-slate-900 tw-shadow-lg dark:tw-border-[#14171c] dark:tw-bg-white" />
          <span className="tw-absolute tw-left-0 tw-top-0 tw-text-lg tw-font-semibold tw-text-teal-700 dark:tw-text-teal-300">{label('stage.window')}</span>
          <span className="tw-absolute tw-left-[52%] tw-top-0 -tw-translate-x-1/2 tw-text-lg tw-font-semibold tw-text-slate-700 dark:tw-text-slate-200">{label('stage.lastDay')}</span>
          <span className="tw-absolute tw-left-[67%] tw-top-[76px] -tw-translate-x-1/2 tw-text-center">
            <span className="tw-block tw-text-2xl tw-font-bold tw-text-amber-600 dark:tw-text-amber-300">{label('stage.grace')}</span>
            <span className="tw-block tw-text-base tw-font-medium tw-text-slate-600 dark:tw-text-slate-300">{label('stage.canComplete')}</span>
          </span>
          <span className="tw-absolute tw-left-[91%] tw-top-[76px] -tw-translate-x-1/2 tw-text-center">
            <LuLock className="tw-mx-auto tw-h-7 tw-w-7 tw-text-rose-500" />
            <span className="tw-mt-1 tw-block tw-whitespace-nowrap tw-text-base tw-font-semibold tw-text-rose-600 dark:tw-text-rose-300">{label('stage.locked')}</span>
          </span>
        </div>
      </Backdrop>
    );
  }

  if (state.variant === 'end') {
    return (
      <Backdrop>
        <Rise show>
          <span className="tw-mx-auto tw-mb-6 tw-grid tw-h-24 tw-w-24 tw-place-items-center tw-rounded-full tw-bg-gradient-to-br tw-from-emerald-400 tw-to-teal-600 tw-text-white tw-shadow-2xl tw-shadow-emerald-500/30">
            <LuCircleCheck className="tw-h-12 tw-w-12" />
          </span>
          <p className="tw-text-5xl tw-font-bold tw-tracking-tight tw-text-slate-900 dark:tw-text-white">{label('stage.endTitle')}</p>
          <p className="tw-mt-4 tw-text-xl tw-text-slate-600 dark:tw-text-slate-300">{label('stage.endText')}</p>
        </Rise>
        <Rise show={p > 0.25} delay={100}>
          <div className="tw-mt-10 tw-flex tw-items-center tw-gap-4">
            <span className="tw-inline-flex tw-h-12 tw-items-center tw-gap-2 tw-rounded-full tw-border tw-border-slate-200 tw-bg-white/80 tw-px-6 tw-text-lg tw-font-semibold tw-text-slate-700 tw-shadow-sm dark:tw-border-white/10 dark:tw-bg-white/[0.06] dark:tw-text-slate-200">
              <LuCircleHelp className="tw-h-5 tw-w-5" />
              {t('profilePage.calendar.help.button')}
            </span>
            <span className="tw-inline-flex tw-h-12 tw-items-center tw-gap-2 tw-rounded-full tw-bg-gradient-to-r tw-from-teal-500 tw-to-cyan-600 tw-px-6 tw-text-lg tw-font-semibold tw-text-white tw-shadow-lg tw-shadow-teal-500/30">
              <LuPlay className="tw-h-5 tw-w-5" />
              {label('cta.button')}
            </span>
          </div>
        </Rise>
      </Backdrop>
    );
  }

  return (
    <Backdrop>
      <Rise show>
        <span className="tw-mx-auto tw-mb-6 tw-grid tw-h-20 tw-w-20 tw-place-items-center tw-rounded-3xl tw-bg-gradient-to-br tw-from-teal-400 tw-to-cyan-600 tw-text-white tw-shadow-2xl tw-shadow-teal-500/30">
          <LuCalendarDays className="tw-h-10 tw-w-10" />
        </span>
        <p className="tw-text-5xl tw-font-bold tw-tracking-tight tw-text-slate-900 dark:tw-text-white">{label('player.title')}</p>
        <p className="tw-mt-4 tw-text-xl tw-text-slate-600 dark:tw-text-slate-300">{label('stage.introText')}</p>
      </Rise>
      <div className="tw-mt-12 tw-flex tw-max-w-[1000px] tw-flex-wrap tw-justify-center tw-gap-3">
        {CHAPTERS.map((key, index) => (
          <Rise key={key} show={p > 0.15 + index * 0.08}>
            <span className="tw-inline-flex tw-items-center tw-gap-2.5 tw-rounded-full tw-border tw-border-white tw-bg-white/80 tw-py-2 tw-pl-2 tw-pr-5 tw-text-base tw-font-semibold tw-text-slate-700 tw-shadow-sm dark:tw-border-white/10 dark:tw-bg-white/[0.06] dark:tw-text-slate-200">
              <span className="tw-grid tw-h-8 tw-w-8 tw-place-items-center tw-rounded-full tw-bg-teal-500/15 tw-text-sm tw-font-bold tw-text-teal-700 dark:tw-text-teal-300">{index + 1}</span>
              {label(`chapters.${key}`)}
            </span>
          </Rise>
        ))}
      </div>
    </Backdrop>
  );
};

TitleScene.propTypes = { state: PropTypes.object.isRequired, p: PropTypes.number.isRequired };

export default TitleScene;
